import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { OL_COLOUR_MELON } from 'src/web/components/Style/colours';
import {
  AddToInstitutionTaskResult,
  BackgroundTaskEnum,
  BackgroundTaskStatus,
} from 'src/resource/BackgroundTaskResource/types';
import { Spinner } from 'src/web/components/Spinner/Spinner';
import { confirmDialog } from 'src/web/components/Modals/ConfirmDialog';
import {
  CourseEnrolment,
  CoursesFilterData,
} from 'src/ReduxStorage/InstitutionAdminSettings/types';
import { capitalizeFirstLetter } from 'src/web/utilities/utilities';
import { ActiveTeamMemberTable } from '../../TeamPage/ViewTeamMembers/ActiveTeamMemberTable/component';
import { ActiveLearnersTable } from '../../LearnersPage/ViewLearners/ActiveLearnersTable/component';
import { Header, PendingTeamAlertMessage } from '../../styles';
import {
  useGetBackgroundTasksApi,
  useDeleteBackgroundTaskApi,
  useGetPaginatedEnrolmentsApi,
  useGetPaginatedPendingInvitations,
} from '../../hooks';
import {
  BackgroundTaskWrapper,
  ErrorInfoWrapper,
  StyledButton,
  StyledIconButton,
  IconWrapper,
} from './styles';
import { PendingTeamMemberTable } from '../../TeamPage/ViewTeamMembers/PendingTeamMemberTable/component';
import { PendingLearnersTable } from '../../LearnersPage/ViewLearners/PendingLearnersTable/component';
import { MemberType } from '../../types';

const AddedMemberDetail: React.FC<{
  institutionPath: string;
  memberType: MemberType;
  allMemberEmails: string[];
  courses: CourseEnrolment[];
}> = ({ institutionPath, allMemberEmails, courses, memberType }) => {
  const {
    data: activeMemberData,
    isLoading: isLoadingActiveMemberData,
    getPaginatedEnrolments,
  } = useGetPaginatedEnrolmentsApi(institutionPath, {
    filterByUserEmails: allMemberEmails,
    filterByStaff: memberType === 'team members',
  });
  const {
    data: pendingMemberData,
    isLoading: isLoadingPendingMemberData,
    getPaginatedPendingInvitations,
  } = useGetPaginatedPendingInvitations(institutionPath, {
    filterByUserEmails: allMemberEmails,
    staff: memberType === 'team members',
  });
  const ActiveMemberTable =
    memberType === 'team members' ? ActiveTeamMemberTable : ActiveLearnersTable;
  const PendingMemberTable =
    memberType === 'team members'
      ? PendingTeamMemberTable
      : PendingLearnersTable;

  return (
    <>
      {
        <>
          <Header>Joined {memberType}</Header>
          <ActiveMemberTable
            allCourses={courses}
            currentInstitutionPath={institutionPath}
            enrolments={activeMemberData.enrolments}
            getPaginatedEnrolments={getPaginatedEnrolments}
            isLoadingData={isLoadingActiveMemberData}
            total={activeMemberData.total}
            totalPages={activeMemberData.totalPages}
            filterByUserEmails={allMemberEmails}
          />
          <hr />
        </>
      }
      {
        <>
          <Header>Pending {memberType}</Header>
          <PendingTeamAlertMessage
            kind="info"
            message={
              <div>
                {capitalizeFirstLetter(memberType)} who appear as pending have
                been invited to the institution, but:
                <ul>
                  <li>Don’t have an account on OpenLearning; or</li>
                  <li>Have an unverified account on OpenLearning.</li>
                </ul>
              </div>
            }
          />
          <PendingMemberTable
            allCourses={courses}
            currentInstitutionPath={institutionPath}
            getPaginatedPendingInvitations={getPaginatedPendingInvitations}
            paginatedPendingInvitations={pendingMemberData}
            isLoading={isLoadingPendingMemberData}
            filterByUserEmails={allMemberEmails}
          />
        </>
      }
    </>
  );
};

export const RecentlyAddedMembers: React.FC<{
  currentInstitutionPath: string;
  memberType: MemberType;
  allCoursesFilterData: CoursesFilterData;
}> = ({ currentInstitutionPath, memberType, allCoursesFilterData }) => {
  const {
    getBackgroundTasks,
    isLoading: isLoadingBackgroundTask,
  } = useGetBackgroundTasksApi(currentInstitutionPath);
  const { deleteBackgroundTask } = useDeleteBackgroundTaskApi();
  const [addMemberTasks, setAddMemberTasks] = useState<
    AddToInstitutionTaskResult[]
  >([]);
  const [showError, setShowError] = useState<{ [taskId: string]: boolean }>({});
  const [showAddedMemberDetail, setShowAddedMemberDetail] = useState<{
    [taskId: string]: boolean;
  }>({});

  const allCourses = Object.keys(allCoursesFilterData).map(
    (coursePath) => allCoursesFilterData[coursePath]
  );

  const getAddedMemberTasks = async (): Promise<void> => {
    const addedMembersToInstitution = await getBackgroundTasks({
      taskType:
        memberType === 'team members'
          ? BackgroundTaskEnum.ADD_TEAM_MEMBER_TO_INSTITUTION
          : BackgroundTaskEnum.ADD_LEARNER_TO_INSTITUTION,
    });
    setAddMemberTasks([
      ...addedMembersToInstitution,
    ] as AddToInstitutionTaskResult[]);
  };

  useEffect(() => {
    getAddedMemberTasks();
  }, [currentInstitutionPath, memberType]);

  return (
    <>
      <Header>Recently added {memberType}</Header>
      {isLoadingBackgroundTask && <Spinner />}
      {addMemberTasks.length === 0 && !isLoadingBackgroundTask && (
        <BackgroundTaskWrapper>
          There are no recently added {memberType}
        </BackgroundTaskWrapper>
      )}
      {addMemberTasks.map(({ id, status, result, params }) => (
        <>
          <BackgroundTaskWrapper>
            <IconWrapper>
              {status === BackgroundTaskStatus.COMPLETED && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  color={OL_COLOUR_MELON}
                  size="lg"
                />
              )}
              {(status === BackgroundTaskStatus.IN_PROGRESS ||
                status === BackgroundTaskStatus.NOT_STARTED) && <Spinner />}
            </IconWrapper>
            <strong>
              {params.invites.length - (result?.unprocessedEmails.length || 0)}{' '}
              {memberType}
            </strong>{' '}
            {status === BackgroundTaskStatus.COMPLETED
              ? 'have been'
              : 'are being'}{' '}
            added to the institution
            <StyledIconButton
              hiddenLabel="Remove this task"
              icon="remove"
              onClick={() => {
                confirmDialog({
                  title: 'Remove this task?',
                  message: 'Are you sure?',
                  cancelLabel: 'Cancel',
                  okHandler: () => {
                    deleteBackgroundTask(id).then(getAddedMemberTasks);
                  },
                });
              }}
            />
            {!!result?.unprocessedEmails.length && (
              <StyledButton
                label={`View ${result.unprocessedEmails.length} errors`}
                icon={showError[id] ? 'chevron-down' : 'chevron-right'}
                colour="danger"
                onClick={() => {
                  setShowError({ ...showError, [id]: !showError[id] });
                }}
              />
            )}
            {status === BackgroundTaskStatus.COMPLETED && (
              <StyledButton
                label="View and assign to courses"
                icon={
                  showAddedMemberDetail[id] ? 'chevron-down' : 'chevron-right'
                }
                colour="kelp"
                onClick={() => {
                  setShowAddedMemberDetail({
                    ...showAddedMemberDetail,
                    [id]: !showAddedMemberDetail[id],
                  });
                }}
              />
            )}
          </BackgroundTaskWrapper>
          {showError[id] && result && (
            <ErrorInfoWrapper>
              {result.unprocessedEmails.map((email) => (
                <div>
                  <strong>{email}</strong> could not be added to the institution
                  – invalid email format
                </div>
              ))}
            </ErrorInfoWrapper>
          )}
          {showAddedMemberDetail[id] && result && (
            <AddedMemberDetail
              allMemberEmails={[
                ...result.autoEnrolledStudents.map(({ email }) => email),
                ...result.alreadyEnrolledStudents.map(({ email }) => email),
                ...result.pendingEnrolledEmails,
              ]}
              courses={allCourses}
              institutionPath={currentInstitutionPath}
              memberType={memberType}
            />
          )}
        </>
      ))}
    </>
  );
};
